import type { RuleBoxFacets, RuleBoxOptionFilter } from "@loot-os/api-contract";
import {
  removeOptionFilter,
  setOptionFilter,
  textChoices,
} from "@loot-os/api-contract/shopifyRuleBoxForm";

import { ChoicePicker } from "./ChoicePicker";
import { fieldValue } from "./fieldValue";

/**
 * The variant option filters: one picker per option the merchant added (Size: M or L), and a
 * select to add another from the options the shop's variants use. Each matches per variant, so a
 * box can take only the size-M variants of a shirt.
 */
export function OptionFilters({
  facets,
  filters,
  onChange,
}: {
  facets: RuleBoxFacets;
  filters: RuleBoxOptionFilter[];
  onChange: (next: RuleBoxOptionFilter[]) => void;
}) {
  const taken = new Set(filters.map((f) => f.name.trim().toLowerCase()));
  const addable = facets.options.filter((o) => !taken.has(o.name.trim().toLowerCase()));
  if (facets.options.length === 0 && filters.length === 0) {
    return <s-text color="subdued">Your products have no options like size or color.</s-text>;
  }
  return (
    <s-stack gap="base">
      {filters.map((filter) => {
        const values =
          facets.options.find(
            (o) => o.name.trim().toLowerCase() === filter.name.trim().toLowerCase(),
          )?.values ?? [];
        return (
          <s-stack key={filter.name} gap="small-200">
            <ChoicePicker
              label={filter.name}
              details={`Only variants with one of these ${filter.name.toLowerCase()} values.`}
              placeholder={`Search ${filter.name.toLowerCase()} values`}
              options={textChoices(values)}
              chosen={filter.values}
              freeText={false}
              noun="values"
              onChange={(next) => onChange(setOptionFilter(filters, filter.name, next))}
            />
            <s-stack direction="inline">
              <s-button
                variant="tertiary"
                tone="critical"
                onClick={() => onChange(removeOptionFilter(filters, filter.name))}
              >
                {`Remove ${filter.name} filter`}
              </s-button>
            </s-stack>
          </s-stack>
        );
      })}
      {addable.length > 0 && (
        <s-select
          // Remounted whenever the filters change, so it shows its placeholder again: the native
          // select keeps the last pick, and picking it again after a removal would not fire.
          key={filters.map((f) => f.name).join("\u0000")}
          label="Filter by an option"
          details="Size, color or any option your variants have."
          value=""
          onChange={(e) => {
            const name = fieldValue(e);
            if (name) onChange(setOptionFilter(filters, name, []));
          }}
        >
          <s-option value="">Choose an option</s-option>
          {addable.map((option) => (
            <s-option key={option.name} value={option.name}>
              {option.name}
            </s-option>
          ))}
        </s-select>
      )}
    </s-stack>
  );
}
